/**
 * Integrations — the outbound services, and whether each still works.
 *
 * Visual Reference: `design/screens/integrations.html` — states default and
 * loading only. Its own note says why there is no third: "**Empty does not
 * apply**: the service list is fixed, and failure is always per-card, never a
 * whole-page error."
 *
 * THREE DELIBERATE DEVIATIONS FROM THE MOCKUP, each with a reason:
 *
 *   1. **Slack and GoHighLevel render "Not connected".** The mockup draws them
 *      connected, with a token, a channel and — for GoHighLevel — a scope
 *      failure. All three are M4 (PRD §7.4: "M1 (email) · M4 (rest)"), Story
 *      11.3's own first criterion says so, and Story 11.1's credential registry
 *      has no tester for them, so a credential for one cannot exist to be
 *      drawn. The card, the pill and the description all render; only the
 *      fiction of a connection does not.
 *
 *   2. **The "Connect" button does not render on a not-connected card.** The
 *      mockup draws one on Webhook. There is nothing behind it until M4, and
 *      DECISION-016 is explicit that a later milestone's control does not
 *      render at M1 — a button that opens nothing "reads as broken rather than
 *      as not-yet-built". The card still says what the service would do, which
 *      is the half that is true today.
 *
 *   3. **`Retry` does not render on a failed delivery.** `POST
 *      /v1/w/{wid}/deliveries/{id}/retry` is declared **M4** in
 *      `docs/api-contract.md`. The row still reads `Failed`, so the fault is
 *      visible; what is absent is the control that cannot act on it.
 *
 * WHAT IS NOT A DEVIATION: the descriptions on the Slack and GoHighLevel cards
 * are written here, because the mockup draws only Webhook's — it had no
 * not-connected state for the other two to copy. They follow Webhook's sentence
 * exactly: what the service would do with a lead, and why that helps. Filed for
 * the next `/design` round-trip.
 *
 * FAILURE IS PER CARD. A test that fails writes its message into that card and
 * nowhere else, and the other three keep working — which is the contract's own
 * wording for this route and the reason this page has no error state.
 *
 * Story 11.3.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPost, ApiFailure } from '../lib/api'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Pill } from '../ui/Pill'
import { Skeleton } from '../ui/Skeleton'

type Integration = {
  kind: string
  label: string
  milestone: string
  status: 'connected' | 'not_connected'
  fingerprint: string | null
  lastUsedAt: number | null
  addedAt: number | null
  addedBy: string | null
  waiting: number
}

type Delivery = {
  id: string
  service: string
  destination: string
  leadName: string | null
  leadId: string | null
  createdAt: number
  status: string
  error: string | null
}

/**
 * What each service would do, in the voice the mockup uses for Webhook.
 *
 * Email's is the one an organisation sees before its first credential; the
 * other three are what the M4 cards say until their milestone arrives. The
 * `ACTION_CATALOGUE` blurb is deliberately not reused — it is one clause for a
 * chooser row ("A channel or a direct message"), and a card has room for the
 * sentence that says why it matters.
 */
const WOULD_DO: Record<string, string> = {
  email: 'Notify a person or a shared inbox the moment a lead is scored, from your own domain and your own sending reputation.',
  slack: 'Post every scored lead into a channel or a direct message, with its band and score, so the right person sees it without opening Leadgible.',
  ghl: 'Create the contact in GoHighLevel and apply a tag from its band, so a qualified lead lands in the pipeline it belongs to.',
  webhook:
    'Post every scored lead to a URL you control, with its band, score and answers, so your own systems can act on it.',
}

/** The mockup labels the mail card by its vendor; the API never does. */
const CARD_LABEL: Record<string, string> = { email: 'Email (Mailgun)' }

const MINUTE = 60_000

function ago(at: number, now: number): string {
  const elapsed = Math.max(0, now - at)
  const minutes = Math.floor(elapsed / MINUTE)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days < 31) return `${days} day${days === 1 ? '' : 's'} ago`
  const months = Math.floor(days / 30)
  return `${months} month${months === 1 ? '' : 's'} ago`
}

const day = (at: number) =>
  new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })

/** "Today, 09:12" · "Yesterday, 17:30" · "23 July, 06:02" — the mockup's three. */
function when(at: number, now: number): string {
  const time = new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  const startOfToday = new Date(now).setHours(0, 0, 0, 0)
  if (at >= startOfToday) return `Today, ${time}`
  if (at >= startOfToday - 86_400_000) return `Yesterday, ${time}`
  return `${day(at)}, ${time}`
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      {children}
    </section>
  )
}

export function Integrations() {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null

  const [integrations, setIntegrations] = useState<Integration[] | null>(null)
  const [deliveries, setDeliveries] = useState<Delivery[]>([])
  const [loading, setLoading] = useState(true)
  /** Per CARD, which is the whole point — one key per kind, never a page flag. */
  const [testing, setTesting] = useState<string | null>(null)
  const [cardError, setCardError] = useState<Record<string, string>>({})
  const [cardOk, setCardOk] = useState<Record<string, boolean>>({})

  const now = Date.now()

  const load = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    /**
     * The two reads are independent and are allowed to disagree: the delivery
     * log failing must not take the service cards down with it, which is the
     * same "per card, never whole-page" rule one level up.
     */
    const [cards, log] = await Promise.allSettled([
      apiGet<{ integrations: Integration[] }>(`/v1/w/${workspaceId}/integrations`),
      apiGet<{ deliveries: Delivery[] }>(`/v1/w/${workspaceId}/deliveries?limit=5`),
    ])
    setIntegrations(cards.status === 'fulfilled' ? cards.value.integrations : null)
    setDeliveries(log.status === 'fulfilled' ? log.value.deliveries : [])
    setLoading(false)
  }, [workspaceId])

  useEffect(() => {
    void load()
  }, [load])

  const test = async (kind: string) => {
    if (!workspaceId || testing) return
    setTesting(kind)
    setCardError((prior) => ({ ...prior, [kind]: '' }))
    setCardOk((prior) => ({ ...prior, [kind]: false }))
    try {
      await apiPost(`/v1/w/${workspaceId}/integrations/${kind}/test`, {})
      setCardOk((prior) => ({ ...prior, [kind]: true }))
      await load()
    } catch (thrown) {
      /**
       * The server's sentence, verbatim. ARCH §4.3 requires it to name the fix
       * — "Your GoHighLevel token doesn't have permission to create contacts.
       * Regenerate it with the contacts.write scope." — and
       * `tests/error-copy.test.ts` pins that exact string against this very
       * mockup. A message this screen reworded could disagree with it.
       */
      setCardError((prior) => ({
        ...prior,
        [kind]:
          thrown instanceof ApiFailure
            ? thrown.message
            : "That connection couldn't be checked just now. Try again in a moment.",
      }))
    } finally {
      setTesting(null)
    }
  }

  const header = (
    <header className="flex flex-col gap-mdx-8">
      <h1 className="m-0 text-mdx-h1 font-mdx-bold leading-mdx-tight tracking-mdx-h1">
        Integrations
      </h1>
      <p className="m-0 max-w-mdx-measure-60 text-mdx-16 text-mdx-slate">
        Keys are stored write-only. Leadgible can use a credential and tell you it still works, but
        nobody — including us — can read one back out.
      </p>
    </header>
  )

  if (loading) {
    return (
      <>
        {header}
        <div className="grid grid-cols-1 gap-mdx-16 lg:grid-cols-2">
          {Array.from({ length: 4 }, (_, index) => (
            <Card key={index}>
              <Skeleton className="h-mdx-22 w-mdx-measure-34 max-w-full" />
              <Skeleton className="h-mdx-16 w-full" />
              <Skeleton className="h-mdx-input w-full rounded-mdx-control" />
            </Card>
          ))}
        </div>
      </>
    )
  }

  return (
    <>
      {header}

      <div className="grid grid-cols-1 gap-mdx-16 lg:grid-cols-2">
        {(integrations ?? []).map((service) => {
          const connected = service.status === 'connected'
          const failure = cardError[service.kind]
          return (
            <section
              key={service.kind}
              className="flex flex-col gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card"
            >
              <div className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-10">
                <span className="min-w-0 flex-auto text-mdx-17 font-mdx-semibold tracking-mdx-h3">
                  {CARD_LABEL[service.kind] ?? service.label}
                </span>
                <Pill tone={connected ? 'hot' : 'neutral'} dot>
                  {connected ? 'Connected' : 'Not connected'}
                </Pill>
              </div>

              {connected ? (
                <span className="text-mdx-15 text-mdx-slate">
                  {service.lastUsedAt === null
                    ? 'Not used yet'
                    : `Last used ${ago(service.lastUsedAt, now)}`}
                  {service.waiting > 0
                    ? ` · ${service.waiting} send${service.waiting === 1 ? '' : 's'} waiting`
                    : ''}
                </span>
              ) : (
                <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
                  {WOULD_DO[service.kind] ?? service.label}
                </span>
              )}

              {connected ? (
                <div className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-12 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12">
                  <span
                    aria-hidden="true"
                    className={`flex size-mdx-evidence-icon shrink-0 items-center justify-center rounded-mdx-control-sm text-mdx-14 font-mdx-bold text-mdx-white ${
                      failure ? 'bg-mdx-error' : 'bg-mdx-hot'
                    }`}
                  >
                    {failure ? '!' : '✓'}
                  </span>
                  <span className="shrink-0 text-mdx-15 font-mdx-semibold">Key</span>
                  <span className="min-w-0 flex-1 tabular-nums text-mdx-15 text-mdx-slate">
                    {service.fingerprint}
                  </span>
                  {service.addedAt ? (
                    <span className="shrink-0 text-mdx-13 text-mdx-slate">
                      Added {day(service.addedAt)}
                      {service.addedBy ? ` by ${service.addedBy}` : ''}
                    </span>
                  ) : null}
                </div>
              ) : null}

              {/**
               * THE PER-CARD FAILURE. It lands here, inside this card, and the
               * other three are untouched — `docs/api-contract.md`: "Failure is
               * per card, never whole-page."
               */}
              {failure ? (
                <p className="m-0 max-w-mdx-measure-52 text-mdx-13 text-mdx-error">{failure}</p>
              ) : null}
              {cardOk[service.kind] ? (
                <p className="m-0 text-mdx-13 text-mdx-hot-ink">
                  That connection is working. Checked just now.
                </p>
              ) : null}

              {connected ? (
                <div className="mt-auto flex flex-wrap gap-mdx-12">
                  <Button
                    variant="secondary"
                    loading={testing === service.kind ? 'Checking…' : undefined}
                    onClick={() => void test(service.kind)}
                  >
                    Test connection
                  </Button>
                  {/**
                   * A LINK, not a form. RULE-006 puts replacing a credential
                   * behind Owner on a different screen, so this card's job is
                   * to send them there rather than to grow a second place a
                   * key can be entered.
                   */}
                  <Button
                    variant="secondary"
                    onClick={() => window.location.assign('/settings/credentials')}
                  >
                    Replace key
                  </Button>
                </div>
              ) : null}
            </section>
          )
        })}
      </div>

      <p className="m-0 max-w-mdx-measure text-mdx-13 text-mdx-slate">
        Only the owner can add or replace a credential. Admins can test a connection and retry a
        delivery.
      </p>

      <Card>
        <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
          <h2 className="m-0 text-mdx-17 font-mdx-semibold tracking-mdx-h3">Recent deliveries</h2>
          {/**
           * "See all deliveries" is NOT rendered — Q-063. The export draws the
           * link with `href="#deliveries"`, and there is no page behind it:
           * Story 9.5 put the delivery log inside the funnel editor's Connect
           * tab, per funnel, and DECISION-018 fixed the eight nav destinations
           * with no `/deliveries` among them. A link that goes nowhere reads as
           * a broken page rather than as a feature living elsewhere.
           */}
          <span className="text-mdx-13 text-mdx-slate">Newest five</span>
        </div>

        {deliveries.length === 0 ? (
          <span className="text-mdx-15 text-mdx-slate">
            Nothing has been sent from this workspace yet. Deliveries appear here as leads arrive.
          </span>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-mdx-4 p-0">
            {deliveries.map((delivery) => (
              <li
                key={delivery.id}
                className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-8 rounded-mdx-control px-mdx-12 py-mdx-12 hover:bg-mdx-ground"
              >
                <span className="min-w-0 flex-1 basis-mdx-row-identity truncate text-mdx-15 text-mdx-ink">
                  {delivery.service} · {delivery.destination}
                </span>
                <span className="min-w-0 flex-auto truncate text-mdx-15 text-mdx-slate">
                  {delivery.leadName ?? '—'}
                </span>
                <span className="shrink-0 text-mdx-13 tabular-nums text-mdx-slate">
                  {when(delivery.createdAt, now)}
                </span>
                <Pill tone={delivery.status === 'Failed' ? 'error' : 'hot'}>{delivery.status}</Pill>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  )
}
